import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "libphonenumber-js";
import { concat, isEmpty } from "lodash";
import { isEmail } from "validator";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";

import Input from "../../../../components/InputComplaints";
import Button from "../../../../components/Button";
import Textarea from "../../../../components/Textarea";
import Checkbox from "../../../../components/Checkbox";
import ValidationError from "../../../../components/ValidationError";
import FormItem from "../../../../components/FormItem";
import Select from "../../../../components/Select";
import styles from "../../../../styles/global.module.css";
import {
  SubLabel,
  SubmitSection,
  PrivacyLabel,
} from "../../../Static/components/ComplaintsAndFeddbackQuestions/style";
import {
  CenteredWithGutters,
  BtnContainer,
  RowContainer,
} from "../../../../components/HCPAdditionalQuestionsForm/styles";

// This is different from the form state values.
// The transformation happens on delegation to the submit handler.
export type NoProviderFoundContactRequestSubmitData = {
  firstName: string;
  lastName: string;
  email: string;
  number: string;
  postcode: string;
  serviceRequired: string;
  fundingType: string | null;
  ageGroup: string;
  details: string;
  termsAndConditions: boolean;
};

const NoProviderFoundForm = (props) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  );
  const [showV2, setShowV2] = useState(false);
  const [v2Token, setV2Token] = useState("");
  const recaptchaV2Ref = useRef<ReCAPTCHA | null>(null);

  const { register, formState, handleSubmit, setValue, reset, setError } =
    useForm<NoProviderFoundContactRequestSubmitData>({
      mode: "all",
      defaultValues: {
        firstName: "",
        lastName: "",
        email: "",
        number: "",
        postcode: "",
        serviceRequired: "",
        fundingType: null,
        ageGroup: "",
        details: "",
        termsAndConditions: false,
      },
    });

  const { isSubmitting, errors } = formState;
  const { filters, ageGroups, fundingTypes, serviceAreas } = props;
  const serviceName = filters && filters.service_areas_list;
  const ageDescription =
    ageGroups && filters.age_groups_list
      ? ageGroups[filters.age_groups_list].code
      : null;
  const fundingName =
    fundingTypes &&
    filters.funding_types_list &&
    fundingTypes[filters.funding_types_list]
      ? fundingTypes[filters.funding_types_list].code
      : null;

  const postcode = filters && filters.postcodes_list;

  // Function to verify recaptcha token
  const verifyToken = async (token, version, action = "submit") => {
    try {
      const response = await fetch("/api/recaptcha/verify-recaptcha/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          token,
          version,
          action: version === "v3" ? action : undefined, // Send action for v3
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: "Unknown error occurred" }));
        throw new Error(
          `Backend verification failed: ${response.status} ${errorData.detail || response.statusText}`
        );
      }
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: "Network error or backend unreachable.",
      };
    }
  };

  const onTokenChange = async (token) => {
    if (token) {
      setV2Token(token);
      setValidationError(null);
    }
  };

  const submitForm = async (
    formData: NoProviderFoundContactRequestSubmitData
  ) => {
    // Function to handle the final successful submission logic
    const handleSuccessfulSubmit = async () => {
      try {
        await props.onSubmit(
          {
            ...formData,
            fundingType:
              formData.fundingType === "i-don-t-know"
                ? null
                : formData.fundingType,
          },
          setError
        );
        reset({}, { keepValues: true });
      } catch (error) {
        setError("root", {
          type: "server",
          message: "reCAPTCHA verification failed. Please try again.",
        });
      }
    };

    // Function to trigger the v2 fallback
    const triggerV2Fallback = (
      message: string = "Please verify you are human"
    ) => {
      setShowV2(true);
      setValidationError(message);
    };

    // Handle the v2 submission
    if (showV2) {
      if (v2Token) {
        const backendResponseV2 = await verifyToken(v2Token, "v2");
        if (backendResponseV2.success) {
          setValidationError(null);
          await handleSuccessfulSubmit();
        } else {
          triggerV2Fallback("reCAPTCHA verification failed. Please try again.");
        }
      } else {
        setValidationError("Please complete the reCAPTCHA to continue.");
      }
      return;
    }

    if (!executeRecaptcha) {
      triggerV2Fallback();
      return;
    }

    try {
      const v3Token = await executeRecaptcha("submit");
      const backendResponseV3 = await verifyToken(v3Token, "v3");
      if (backendResponseV3.success) {
        setValidationError(null);
        await handleSuccessfulSubmit();
      } else {
        triggerV2Fallback();
      }
    } catch (error) {
      triggerV2Fallback(
        "Could not verify reCAPTCHA. Please verify you are human"
      );
    }
  };

  useEffect(() => {
    if (ageDescription) setValue("ageGroup", ageDescription);
    if (serviceName) setValue("serviceRequired", serviceName);
    if (ageDescription) setValue("ageGroup", ageDescription);
    if (fundingName) setValue("fundingType", fundingName);
    if (postcode) setValue("postcode", postcode);
  }, [serviceName, ageDescription, fundingName, postcode, setValue]);

  return (
    <CenteredWithGutters>
      <form onSubmit={handleSubmit(submitForm)}>
        <RowContainer>
          {window.innerWidth > 768 ? (
            <FormItem
              style={{ marginRight: "24px" }}
              className={styles.halfWidthDesktop}
            >
              <SubLabel>Your first name*</SubLabel>
              <Input
                input={{
                  type: "text",
                  ...register("firstName", { required: true }),
                }}
                meta={{
                  submitFailed: !!errors.firstName,
                  error: "First name is required",
                }}
              />
            </FormItem>
          ) : (
            <FormItem>
              <SubLabel>Your first name*</SubLabel>
              <Input
                input={{
                  type: "text",
                  ...register("firstName", { required: true }),
                }}
                meta={{
                  submitFailed: !!errors.firstName,
                  error: "First name is required",
                }}
              />
            </FormItem>
          )}
          <FormItem className={styles.halfWidthDesktop}>
            <SubLabel>Your surname</SubLabel>
            <Input input={{ type: "text", ...register("lastName") }} />
          </FormItem>
        </RowContainer>
        <FormItem>
          <SubLabel>Email*</SubLabel>
          <Input
            input={{
              type: "text",
              ...register("email", {
                required: true,
                validate: (val) => (isEmail(val) ? undefined : "Invalid URL"),
              }),
            }}
            meta={{
              submitFailed: !!errors.email,
              error: "Please enter a valid email",
            }}
          />
        </FormItem>
        <RowContainer>
          {window.innerWidth > 768 ? (
            <FormItem
              className={styles.halfWidthDesktop}
              style={{ marginRight: "24px" }}
            >
              <SubLabel>Phone*</SubLabel>
              <Input
                input={{
                  type: "text",
                  ...register("number", {
                    required: true,
                    validate: (val) => isValidPhoneNumber(val, "AU"),
                  }),
                }}
                meta={{
                  submitFailed: !!errors.number,
                  error: "Please enter a valid phone number",
                }}
              />
            </FormItem>
          ) : (
            <FormItem>
              <SubLabel>Phone*</SubLabel>
              <Input
                input={{
                  type: "text",
                  ...register("number", {
                    required: true,
                    validate: (val) => isValidPhoneNumber(val, "AU"),
                  }),
                }}
                meta={{
                  submitFailed: !!errors.number,
                  error: "Please enter a valid phone number",
                }}
              />
            </FormItem>
          )}
          <FormItem className={styles.halfWidthDesktop}>
            <SubLabel>Postcode or suburb</SubLabel>
            <Input
              input={{
                type: "text",
                ...register("postcode", { required: true }),
              }}
              meta={{
                submitFailed: !!errors.postcode,
                error: "Postcode is required",
              }}
            />
          </FormItem>
        </RowContainer>
        <FormItem>
          <SubLabel emphasis htmlFor="serviceRequired">
            Service required
          </SubLabel>
          {!!serviceAreas && !isEmpty(serviceAreas) ? (
            <Select
              input={{
                placeholder: "Select from list",
                type: "text",
                ...register("serviceRequired", { required: true }),
              }}
              meta={{
                submitFailed: !!errors.serviceRequired,
                error: "Please select a service you are searching",
              }}
              fieldOptions={Object.keys(serviceAreas).map((key) => ({
                label: serviceAreas[key].name,
                value: serviceAreas[key].code,
              }))}
            />
          ) : (
            <div>Loading ...</div>
          )}
        </FormItem>
        <RowContainer>
          {window.innerWidth > 768 ? (
            <FormItem
              className={styles.halfWidthDesktop}
              style={{ marginRight: "24px" }}
            >
              <SubLabel emphasis htmlFor="ageGroup">
                Age of recipient
              </SubLabel>
              {!!ageGroups && !isEmpty(ageGroups) ? (
                <Select
                  input={{
                    placeholder: "Select from list",
                    type: "text",
                    ...register("ageGroup", { required: true }),
                  }}
                  meta={{
                    submitFailed: !!errors.ageGroup,
                    error: "Please select the age group",
                  }}
                  fieldOptions={Object.keys(ageGroups).map((key) => ({
                    label: ageGroups[key].description,
                    value: ageGroups[key].code,
                  }))}
                />
              ) : (
                <div>Loading ...</div>
              )}
            </FormItem>
          ) : (
            <FormItem className={styles.halfWidthDesktop}>
              <SubLabel emphasis htmlFor="ageGroup">
                Age of recipient
              </SubLabel>
              {!!ageGroups && !isEmpty(ageGroups) ? (
                <Select
                  input={{
                    placeholder: "Select from list",
                    type: "text",
                    ...register("ageGroup", { required: true }),
                  }}
                  meta={{
                    submitFailed: !!errors.ageGroup,
                    error: "Please select your relationship to the participant",
                  }}
                  fieldOptions={Object.keys(ageGroups).map((key) => ({
                    label: ageGroups[key].description,
                    value: ageGroups[key].code,
                  }))}
                />
              ) : (
                <div>Loading ...</div>
              )}
            </FormItem>
          )}
          <FormItem className={styles.halfWidthDesktop}>
            <SubLabel emphasis htmlFor="fundingType">
              Funding type
            </SubLabel>
            {!!fundingTypes && !isEmpty(fundingTypes) ? (
              <Select
                input={{
                  placeholder: "Select from list",
                  type: "text",
                  ...register("fundingType", { required: true }),
                }}
                meta={{
                  submitFailed: !!errors.fundingType,
                  error: "Please select a funding type",
                }}
                fieldOptions={concat(
                  Object.keys(fundingTypes).map((key) => ({
                    label: fundingTypes[key].name,
                    value: fundingTypes[key].code,
                  })),
                  { label: "I don't know", value: "i-don-t-know" }
                )}
              />
            ) : (
              <div>Loading ...</div>
            )}
          </FormItem>
        </RowContainer>
        <FormItem>
          <SubLabel>
            Is there any other information that might help us to find you
            services
          </SubLabel>
          <Textarea
            className={styles.fullWidth}
            style={{ background: "white" }}
            input={{
              type: "text",
              ...register("details", { required: false }),
            }}
          />
        </FormItem>
        <FormItem style={{ display: "flex", flexDirection: "row" }}>
          <Checkbox
            input={{
              type: "checkbox",
              ...register("termsAndConditions", { required: true }),
            }}
            meta={{
              submitFailed: !!errors.termsAndConditions,
              error:
                "Please agree to the terms and conditions before proceeding",
            }}
          />
          <SubLabel>
            I agree to the Karista{" "}
            <a href="/terms-and-conditions" target="_blank">
              Terms and Conditions
            </a>{" "}
            and{" "}
            <a href="/privacy-policy" target="_blank">
              Privacy Policy
            </a>
          </SubLabel>
        </FormItem>
        {showV2 && (
          <ReCAPTCHA
            ref={recaptchaV2Ref}
            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_V2_SITE_KEY}
            onChange={onTokenChange}
            onExpired={() => {
              setValidationError(
                "reCAPTCHA challenge expired. Please try again."
              );
              if (recaptchaV2Ref.current) recaptchaV2Ref.current.reset();
            }}
            onErrored={() => {
              setValidationError(
                "Error loading recaptcha. Please reload the page."
              );
            }}
          />
        )}
        {validationError && (
          <FormItem>
            <ValidationError
              meta={{ submitFailed: true, error: validationError }}
            />
          </FormItem>
        )}
        {errors.root && (
          <FormItem>
            <ValidationError
              meta={{
                submitFailed: true,
                error:
                  "Unable to submit form. Please check all the fieds and try again.",
              }}
            />
          </FormItem>
        )}
        <SubmitSection>
          <PrivacyLabel>
            We value your privacy and are committed to keeping your information
            confidential. The details you provide will not be shared with anyone
            outside of our organisation. We will only share your information
            with a trusted provider if you give us explicit consent to do so.
          </PrivacyLabel>
          <BtnContainer>
            <Button disabled={isSubmitting} fullWidth type="submit">
              Submit
            </Button>
          </BtnContainer>
        </SubmitSection>
      </form>
    </CenteredWithGutters>
  );
};

export default NoProviderFoundForm;
